import { useEffect, useState } from "react";
import {
  PennButton,
  PennField,
  PennModal,
  PennText,
  PennTextarea,
} from "@/components/ui";
import { useAppSelector } from "@/stores/hooks";
import { trackEvent } from "@penn/web-common";
import {
  useCreateTask,
  useWriteSnapshot,
  useWriteTaskApplications,
} from "@/hooks/useTasks";
import { useAddManualLineItem } from "@/hooks/useManualLineItems";
import { useConfigureSelectionPreview } from "@/hooks/useConfigureSelectionPreview";
import { buildRfqSnapshot, toSnapshotApplications } from "@/utils/rfq-snapshot";
import { readStoredWorkFor } from "@penn/web-common";
import { getApiErrorMessage } from "@penn/web-common";
import type { ApplicationDetail } from "@/api/applications";
import type { RegionCode } from "@/api/filter";
import type { RfqFilters, SnapshotSelectedOptionInput } from "@/api/tasks";
import type { DocManualLineItem } from "@/api/quoteDocuments";

/** Selection context for a one-shot RFQ (POC configure → task + snapshot). */
export type RfqContext = {
  solutionProductId: string;
  solutionName?: string | null;
  solutionMachineType?: string | null;
  regionCode: string;
  applications: ApplicationDetail[];
  /** Ids Configure would query with — may be T-Series A/B scoped, not base ids. */
  applicationIds?: string[];
  selectedOptions?: SnapshotSelectedOptionInput[];
  selectedPartNos?: string[];
  /** Manual line items added in the Configure popup (persisted on submit). */
  manualLineItems?: DocManualLineItem[];
  /** Compare-card MSPe auto-feed toggle for this machine (D9). */
  mspAutoFeedEnabled?: boolean;
  /** Comparison cost preview `summary.requiredMachines` for this machine. */
  requiredMachines?: number;
  /** Set when `selectedOptions` came from a Configure submit — see the slice. */
  optionsFrozen?: boolean;
  /** Filter chips + compare set frozen into the snapshot (POC parity, D2). */
  filters?: RfqFilters | null;
};

type SupportModalProps = {
  open: boolean;
  onClose: () => void;
  /** When present, the request freezes this selection as a task snapshot. */
  rfqContext?: RfqContext | null;
};

function applicationRevisionKey(
  apps: ReadonlyArray<{ applicationId: string; updatedAt: string }>,
): string {
  return [...apps]
    .sort((a, b) => a.applicationId.localeCompare(b.applicationId))
    .map((app) => `${app.applicationId}:${app.updatedAt}`)
    .join("|");
}

// =============================================================================
// Request Quotation / Support — POC parity.
//
// The POC popup carries NO email/company/region inputs: the task is created
// from the signed-in user's profile (requestedBy* are filled server-side from
// the JWT identity). It only shows the frozen selection summary + a free-text
// "Additional information" box, exactly like the POC screenshot.
// =============================================================================

export const SupportModal = ({
  open,
  onClose,
  rfqContext,
}: SupportModalProps) => {
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const user = useAppSelector((s) => s.auth.user);
  const lastConfigured = useAppSelector((s) => s.selector.lastConfigured);
  const selectedApps = useAppSelector((s) => s.selector.selectedApps);
  const selectedAppDetails = useAppSelector(
    (s) => s.selector.selectedAppDetails,
  );
  const region = useAppSelector((s) => s.auth.selectedRegion);

  // Auto-context: when the parent didn't pass an rfqContext, fall back to the
  // user's current Selector workspace state in Redux (POC parity — the navbar
  // / floating Request Support popup auto-fills SOLUTION/MACHINE TYPE/INCLUDED
  // from the most recent Configure click + saved applications).
  const effectiveContext: RfqContext | null = rfqContext
    ? rfqContext
    : lastConfigured
      ? {
          solutionProductId: lastConfigured.solutionProductId,
          solutionName: lastConfigured.solutionName,
          solutionMachineType: lastConfigured.solutionMachineType,
          regionCode: lastConfigured.region,
          applications: lastConfigured.applications,
          applicationIds: lastConfigured.applicationIds,
          selectedOptions: lastConfigured.selectedOptions,
          selectedPartNos: lastConfigured.selectedPartNos,
          manualLineItems: lastConfigured.manualLineItems,
          mspAutoFeedEnabled: lastConfigured.mspAutoFeedEnabled,
          requiredMachines: lastConfigured.requiredMachines,
          optionsFrozen: lastConfigured.optionsFrozen,
          filters: lastConfigured.filters,
        }
      : selectedAppDetails.length > 0 && region
        ? {
            solutionProductId: "",
            solutionName: null,
            solutionMachineType: null,
            regionCode: region,
            applications: selectedAppDetails,
            selectedOptions: [],
            selectedPartNos: [],
            manualLineItems: [],
          }
        : null;

  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submittedWorkForKind, setSubmittedWorkForKind] = useState<
    "self" | "registered" | "other"
  >("self");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const createTask = useCreateTask();
  const writeSnapshot = useWriteSnapshot();
  const writeTaskApplications = useWriteTaskApplications();
  const addManualLineItem = useAddManualLineItem();
  const submitting =
    createTask.isPending ||
    writeSnapshot.isPending ||
    writeTaskApplications.isPending ||
    addManualLineItem.isPending;

  // A snapshot is frozen whenever a solution machine is configured — even with
  // no application (a quotation/support request for the machine + options only,
  // POC parity). The cost engine is skipped server-side when there is nothing
  // costable, and the snapshot still lets the task be re-opened in the selector.
  const hasSnapshot =
    !!effectiveContext && !!effectiveContext.solutionProductId;

  // Opened from the Selector rather than from inside Configure: the machine is
  // known but its option selection has not been frozen yet, so derive the SAME
  // configuration Configure would show (persisted draft + family auto-select).
  // Keeps the INCLUDED counts and the frozen snapshot identical either way.
  const previewRegion = (effectiveContext?.regionCode ??
    region) as RegionCode | null;
  const needsPreview =
    hasSnapshot && !effectiveContext?.optionsFrozen && !!previewRegion;
  const previewState = useConfigureSelectionPreview({
    productId: needsPreview
      ? (effectiveContext?.solutionProductId ?? null)
      : null,
    region: previewRegion ?? "EU",
    applicationIds:
      effectiveContext?.applicationIds ??
      effectiveContext?.applications.map((a) => a.applicationId) ??
      [],
    mspAutoFeedEnabled: effectiveContext?.mspAutoFeedEnabled ?? true,
    requiredMachines: effectiveContext?.requiredMachines ?? 1,
    enabled: open && needsPreview,
  });
  const selectedOptions = needsPreview
    ? (previewState.data?.selectedOptions ?? [])
    : (effectiveContext?.selectedOptions ?? []);
  const selectedPartNos = needsPreview
    ? (previewState.data?.selectedPartNos ?? [])
    : (effectiveContext?.selectedPartNos ?? []);
  const manualLineItems = needsPreview
    ? (previewState.data?.manualLineItems ?? [])
    : (effectiveContext?.manualLineItems ?? []);
  const previewLoading = needsPreview && previewState.isLoading;
  const previewError = needsPreview && previewState.isError;

  // Selecting/editing applications loads their full rows asynchronously. Do
  // not let the popup freeze an older detail row during that short transition.
  const applicationsLoading =
    !rfqContext &&
    applicationRevisionKey(selectedApps) !==
      applicationRevisionKey(selectedAppDetails);

  useEffect(() => {
    if (!open) return;
    setSubmitted(false);
    setSubmittedWorkForKind("self");
    setErrorMsg(null);
    setMessage("");
    trackEvent("rfq_modal_opened", {
      region: effectiveContext?.regionCode ?? region ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once per open
  }, [open]);

  const handleSubmit = async () => {
    setErrorMsg(null);
    try {
      // Work-For: when a staff member is acting on behalf of a customer, the
      // task must carry that target so it (a) is attributed to the customer in
      // the Tasks/My-RFQs list and side panel, and (b) the quote is addressed to
      // them. The header-based Work-For context is NOT consumed by POST /v1/tasks
      // (no @WorkFor() there), so we forward the selection explicitly. §D.7 XOR:
      // registered → workingForUserId; "other" (unregistered email) →
      // workingForEmail; never both. requestedBy* stays the signed-in submitter.
      const workFor = readStoredWorkFor();
      const workingForUserId =
        workFor.kind === "registered" ? workFor.userId : null;
      const workingForEmail = workFor.kind === "other" ? workFor.email : null;

      const task = await createTask.mutateAsync({
        comment: message.trim() || null,
        workingForUserId,
        workingForEmail,
        // POC parity: requester identity comes from the signed-in profile,
        // not free-text fields. Server backfills from the JWT when null.
        requestedByEmail: user?.email ?? null,
        requestedByFirstName: user?.firstName ?? null,
        requestedByLastName: user?.lastName ?? null,
        requestedByPhone: user?.phone ?? null,
        requestedByCompanyName: user?.companyName ?? null,
        requestedByCompanyAddress: user?.companyAddress ?? null,
      });

      if (hasSnapshot && effectiveContext) {
        await writeSnapshot.mutateAsync({
          taskId: task.taskId,
          body: buildRfqSnapshot({
            solutionProductId: effectiveContext.solutionProductId,
            solutionName: effectiveContext.solutionName,
            solutionMachineType: effectiveContext.solutionMachineType,
            regionCode: effectiveContext.regionCode,
            applications: effectiveContext.applications,
            selectedOptions,
            selectedPartNos,
            filters: effectiveContext.filters,
          }),
        });

        // Persist any manual line items added in the Configure popup (the task is
        // created first, then the manual lines are attached to it).
        for (const m of manualLineItems) {
          await addManualLineItem.mutateAsync({
            taskId: task.taskId,
            body: {
              description: m.description,
              price: m.price,
              qty: m.qty,
              currency: m.currency,
              isManual: true,
              partNo: m.partNo ?? null,
              category: m.category ?? null,
              itemName: m.itemName ?? null,
            },
          });
        }
      } else {
        // No machine selected, but the customer attached applications — freeze
        // them on the task (applications only, no snapshot row) so they appear
        // in the support-response document + modal, while the task stays in the
        // "Response to General support" flow (hasSnapshot stays false). Prefer
        // the frozen RFQ context, else the current Selector selection, so a
        // missing selectedRegion (external users never open the region picker)
        // or a stale Configure context never drops the applications.
        const apps =
          effectiveContext && effectiveContext.applications.length > 0
            ? effectiveContext.applications
            : selectedAppDetails;
        if (apps.length > 0) {
          await writeTaskApplications.mutateAsync({
            taskId: task.taskId,
            applications: toSnapshotApplications(apps),
          });
        }
      }
      setSubmittedWorkForKind(workFor.kind);
      setSubmitted(true);
    } catch (e: unknown) {
      setErrorMsg(
        getApiErrorMessage(
          e,
          "Could not submit your request. Please try again.",
        ),
      );
    }
  };

  const optionsCount = selectedOptions.length || selectedPartNos.length;
  const apps = effectiveContext?.applications ?? [];
  const appsCount = apps.length;

  // FE validation: an empty support request with no context is not useful and
  // POC's own server hard-500s on it. Require a message, an application or a
  // selected solution snapshot. Sign-in is also required server-side.
  const messageEmpty = message.trim().length === 0;
  const hasContext = hasSnapshot || appsCount > 0;
  const submitDisabled =
    submitting ||
    previewLoading ||
    previewError ||
    applicationsLoading ||
    !isAuthenticated ||
    (messageEmpty && !hasContext);

  const footer = submitted ? (
    <div className="flex items-center justify-end">
      <PennButton variant="primary" size="sm" onClick={onClose}>
        Done
      </PennButton>
    </div>
  ) : (
    <div className="flex items-center justify-end gap-2">
      <PennButton
        variant="secondary"
        size="sm"
        onClick={onClose}
        disabled={submitting}
      >
        Cancel
      </PennButton>
      <PennButton
        variant="primary"
        size="sm"
        onClick={handleSubmit}
        disabled={submitDisabled}
      >
        {submitting
          ? "Submitting…"
          : previewLoading || applicationsLoading
            ? "Updating selection…"
            : "Submit request"}
      </PennButton>
    </div>
  );
  // POC parity: show up to 4 names then "(+K more)".
  const appsSummary = (() => {
    if (appsCount === 0) return "No applications selected";
    const names = apps
      .slice(0, 4)
      .map((a) => a.name?.trim() || "(unnamed application)");
    const tail = appsCount > 4 ? ` (+${appsCount - 4} more)` : "";
    return `${names.join(", ")}${tail}`;
  })();

  return (
    <PennModal
      open={open}
      onClose={onClose}
      title="Request Quotation / Support"
      subtitle="We will create a task including your current applications and configuration selections."
      footer={footer}
    >
      {submitted ? (
        <div className="p-6">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
            <PennText as="p" size="sm" color="slate-900" font="semibold">
              Request submitted.
            </PennText>
            <PennText as="p" size="sm" color="slate-500" className="mt-1">
              Your request and a snapshot of your selection were recorded. Our
              sales/engineering team has been notified and will follow up.
            </PennText>
            <PennText as="p" size="xs" color="slate-500" className="mt-2">
              {submittedWorkForKind === "self"
                ? 'You can track it under "My RFQs / Quotes".'
                : submittedWorkForKind === "registered"
                  ? 'It is in the selected customer\'s "My RFQs / Quotes"; you can view it while working for them.'
                  : 'You can view it under "My RFQs / Quotes" while working for this email; the customer can access it after registering.'}
            </PennText>
          </div>
        </div>
      ) : (
        <div className="p-6 space-y-4">
          {!isAuthenticated && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
              <PennText as="p" size="sm" color="slate-900">
                Please sign in to submit a request.
              </PennText>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <PennText
                as="p"
                size="xs"
                color="slate-500"
                className="uppercase"
              >
                Solution
              </PennText>
              <PennText
                as="p"
                size="sm"
                color="slate-900"
                font="medium"
                className="mt-1"
              >
                {effectiveContext?.solutionName ??
                  effectiveContext?.solutionProductId ??
                  "Solution"}
              </PennText>
            </div>
            <div>
              <PennText
                as="p"
                size="xs"
                color="slate-500"
                className="uppercase"
              >
                Machine type
              </PennText>
              <PennText
                as="p"
                size="sm"
                color="slate-900"
                font="medium"
                className="mt-1"
              >
                {effectiveContext?.solutionMachineType || "N/A"}
              </PennText>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <PennText
              as="p"
              size="xs"
              color="slate-500"
              className="uppercase"
            >
              Included
            </PennText>
            <div aria-live="polite">
              <PennText as="p" size="sm" color="slate-900" className="mt-1">
                {applicationsLoading
                  ? "Applications: updating…"
                  : previewLoading
                    ? `Applications: ${appsCount} • Selected options: updating… • ${appsSummary}`
                    : `Applications: ${appsCount} • Selected options: ${optionsCount} • ${appsSummary}`}
              </PennText>
              {previewError && (
                <PennText as="p" size="xs" color="error" className="mt-2">
                  Configuration options could not be refreshed. Close and reopen
                  this request before submitting.
                </PennText>
              )}
            </div>
          </div>

          <PennField label="Additional information" htmlFor="support-message">
            <PennTextarea
              id="support-message"
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add any context that helps us prepare your quotation/support request (e.g., target date, special requirements, constraints, commercial notes)."
            />
            {/* {isAuthenticated && messageEmpty && !hasContext ? (
              <PennText as="p" size="xs" color="error" className="mt-2">
                Add a message or select at least one application before submitting.
              </PennText>
            ) : (
              <PennText as="p" size="xs" color="slate-500" className="mt-2">
                Your request will be recorded with a full snapshot of your selected applications and configuration.
              </PennText>
            )} */}
          </PennField>

          {errorMsg && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3">
              <PennText as="p" size="sm" color="slate-900">
                {errorMsg}
              </PennText>
            </div>
          )}
        </div>
      )}
    </PennModal>
  );
};
